
import { mapState } from 'vuex';
import { TRANSPARENT_IMAGE_URL } from '@/constants/IMAGE_URLS';

export default {
  name: 'AppReviewMedium',
  props: {
    review: {
      type: Object,
      required: true
    },
    mediaPosition: {
      type: Number,
      default: null,
      validator: v => v === null || [1, 2, 3, 4].includes(v)
    },
    grayscale: { type: Boolean, default: false },
    borderRadius: {
      type: Number,
      default: 4
    },
    frame: {
      type: String,
      default: 'rounded',
      validator: v => ['sharp', 'rounded', 'circle'].includes(v)
    },
    shape: {
      type: String,
      default: 'square',
      validator: v => ['original', 'square', 'vertical'].includes(v)
    },
    version: {
      type: String,
      required: true,
      validator: v =>
        ['original', 'gallery', 'portrait', 'thumbnail'].includes(v)
    },
    videoEagerLoading: {
      type: Boolean,
      default: false
    },
    showPreviewVideo: {
      type: Boolean,
      default: true
    },
    compactOverlay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      MEDIUM_SIZE: {
        SMALL: 'small',
        MEDIUM: 'medium',
        LARGE: 'large'
      },
      currentMediumSize: null,
      resizeObserver: null
    };
  },
  computed: {
    ...mapState('widget', ['widget']),
    ...mapState('reviewSettings', ['reviewSettings']),
    video() {
      const videoIdx = this.mediaPosition ? this.mediaPosition - 1 : 0;
      return this.review.videos[videoIdx];
    },
    videoUrl() {
      return this.showPreviewVideo ? this.video?.preview_url : this.video?.url;
    },
    image() {
      const imageIdx = this.mediaPosition
        ? this.mediaPosition - this.review.videos.length - 1
        : 0;
      return (
        this.review.images[imageIdx] || {
          [this.imageUrlAttr]: TRANSPARENT_IMAGE_URL
        }
      );
    },
    imageUrlAttr() {
      switch (this.version) {
        case 'original':
          return 'url';
        case 'gallery':
          return 'gallery_url';
        case 'portrait':
          return 'portrait_url';
        default:
          return 'thumbnail_url';
      }
    },
    isSocialMediaType() {
      return !!this.review.social_media_type;
    },
    isCurrentMediumSizeLarge() {
      return this.currentMediumSize === this.MEDIUM_SIZE.LARGE;
    },
    enableUnifiedThumbnailIcons() {
      return this.reviewSettings.enable_unified_thumbnail_icons;
    },
    showSocialMediaIcon() {
      if (this.shape === 'vertical') return false;
      return (
        this.isSocialMediaType &&
        this.currentMediumSize !== this.MEDIUM_SIZE.SMALL
      );
    },
    // TODO: 통합 아이콘 전면 전환 후 아이콘 병렬 배치 겹침 방지 조건
    // `!(showVideoPlayIcon && MEDIUM_SIZE.SMALL)` 정리
    showMediaCountIcon() {
      if (this.shape === 'vertical') return false;
      if (
        this.showVideoPlayIcon &&
        this.currentMediumSize === this.MEDIUM_SIZE.SMALL
      )
        return false;
      return this.mediaPosition === null && this.review.media_count > 1;
    },
    showVideoPlayIcon() {
      if (this.shape === 'vertical') return false;

      const isVideoThumbnail =
        !!this.video &&
        (!this.reviewSettings.thumbnail_video_autoplay || !this.videoUrl);
      const isSocialMediaVideo =
        this.isSocialMediaType && this.review.review_source_has_video;

      return isVideoThumbnail || isSocialMediaVideo;
    },
    styleProp() {
      let currentBorderRadius;
      switch (this.frame) {
        case 'rounded':
          currentBorderRadius = `${this.borderRadius}px`;
          break;
        case 'circle':
          currentBorderRadius = '100%';
          break;
        default:
          currentBorderRadius = '0';
      }
      return { '--border-radius': currentBorderRadius };
    }
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width < 48) {
          this.currentMediumSize = this.MEDIUM_SIZE.SMALL;
        } else if (48 <= width && width < 64) {
          this.currentMediumSize = this.MEDIUM_SIZE.MEDIUM;
        } else {
          this.currentMediumSize = this.MEDIUM_SIZE.LARGE;
        }
      }
    });
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    this.resizeObserver.disconnect();
  }
};
